import * as THREE from 'three'
import {
	OrbitControls
} from 'three/examples/jsm/controls/OrbitControls.js'
import {
	RoundedBoxGeometry
} from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'

const RANGE = {
	width: [120, 420],
	height: [120, 360]
}

const BACK_WALL_FRONT_YAW = Math.PI
const RECESS_SIDE_WALL_WIDTH = 0.001
const RECESS_LIGHTING_REVEAL = 0.16
const INSIDE_CURTAIN_CLEARANCE = 0.08
const OUTSIDE_CURTAIN_OVERLAP = 0.36

export function createCurtainStudio(container) {
	const scene = new THREE.Scene()
	const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 80)
	const renderer = new THREE.WebGLRenderer({
		antialias: true,
		alpha: false,
		powerPreference: 'high-performance'
	})

	renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
	renderer.outputColorSpace = THREE.SRGBColorSpace
	renderer.toneMapping = THREE.ACESFilmicToneMapping
	renderer.toneMappingExposure = 1.08
	renderer.shadowMap.enabled = true
	renderer.shadowMap.type = THREE.PCFSoftShadowMap
	renderer.domElement.className = 'curtain-studio-canvas'
	renderer.domElement.style.display = 'block'
	renderer.domElement.style.width = '100%'
	renderer.domElement.style.height = '100%'
	container.appendChild(renderer.domElement)

	const controls = new OrbitControls(camera, renderer.domElement)
	controls.enableDamping = true
	controls.dampingFactor = 0.06
	controls.enablePan = false
	controls.rotateSpeed = 0.46
	controls.zoomSpeed = 0.42
	controls.minPolarAngle = Math.PI * 0.38
	controls.maxPolarAngle = Math.PI * 0.58

	const root = new THREE.Group()
	root.name = 'custom_curtain_wall_scene'
	root.rotation.y = BACK_WALL_FRONT_YAW
	scene.add(root)

	const materials = createMaterials()
	const parts = createParts(root, materials)
	const lights = createLights(scene)

	let frameId = null
	let layout = null
	let targetYaw = BACK_WALL_FRONT_YAW
	let targetPitch = 0
	const pointer = new THREE.Vector2(0, 0)

	function resize() {
		const width = Math.max(container.clientWidth, 1)
		const height = Math.max(container.clientHeight, 1)

		renderer.setSize(width, height, false)
		camera.aspect = width / height
		camera.updateProjectionMatrix()

		if (layout) {
			fitCamera(camera, controls, layout, container)
		}
	}

	function update(config) {
		layout = buildLayout(config)
		updateMaterialColors(materials, config)
		applyLayout(parts, layout, config)
		scene.background = new THREE.Color(config.wallColor || '#d1d2ce')
		lights.windowGlow.color.set(shade(config.wallColor, 0.18))
		fitCamera(camera, controls, layout, container)
	}

	function animate() {
		frameId = requestAnimationFrame(animate)

		root.rotation.y += (targetYaw - root.rotation.y) * 0.045
		root.rotation.x += (targetPitch - root.rotation.x) * 0.045

		controls.update()
		renderer.render(scene, camera)
	}

	function onPointerMove(event) {
		const rect = container.getBoundingClientRect()
		pointer.x = (event.clientX - rect.left) / Math.max(rect.width, 1) - 0.5
		pointer.y = (event.clientY - rect.top) / Math.max(rect.height, 1) - 0.5
		targetYaw = BACK_WALL_FRONT_YAW + pointer.x * 0.038
		targetPitch = -pointer.y * 0.012
	}

	window.addEventListener('resize', resize)
	container.addEventListener('pointermove', onPointerMove)

	resize()
	animate()

	return {
		update,

		resetView() {
			targetYaw = BACK_WALL_FRONT_YAW
			targetPitch = 0

			root.rotation.x = 0
			root.rotation.y = BACK_WALL_FRONT_YAW
			root.rotation.z = 0

			if (layout) {
				fitCamera(camera, controls, layout, container)
			}

			controls.update()
			renderer.render(scene, camera)
		},

		dispose() {
			if (frameId) {
				cancelAnimationFrame(frameId)
			}

			window.removeEventListener('resize', resize)
			container.removeEventListener('pointermove', onPointerMove)
			controls.dispose()

			root.traverse((object) => {
				if (object.geometry) {
					object.geometry.dispose()
				}

				if (object.material) {
					const materialList = Array.isArray(object.material) ? object.material : [object.material]
					materialList.forEach((material) => {
						if (material.map) material.map.dispose()
						if (material.bumpMap) material.bumpMap.dispose()
						material.dispose()
					})
				}
			})

			renderer.dispose()

			if (renderer.domElement.parentNode) {
				renderer.domElement.parentNode.removeChild(renderer.domElement)
			}
		}
	}
}

function createLights(scene) {
	const hemi = new THREE.HemisphereLight(0xffffff, 0x7f817b, 1.02)
	scene.add(hemi)

	const key = new THREE.DirectionalLight(0xffffff, 2.35)
	key.position.set(-3.6, 4.8, 4.6)
	key.castShadow = true
	key.shadow.mapSize.set(2048, 2048)
	key.shadow.camera.left = -5
	key.shadow.camera.right = 5
	key.shadow.camera.top = 5
	key.shadow.camera.bottom = -5
	key.shadow.camera.near = 0.8
	key.shadow.camera.far = 12
	scene.add(key)

	const windowGlow = new THREE.PointLight(0xf4f0e5, 1.55, 8.5, 1.9)
	windowGlow.position.set(0, 0.8, 1.35)
	scene.add(windowGlow)

	const gardenFill = new THREE.DirectionalLight(0xc6d8aa, 0.55)
	gardenFill.position.set(1.4, -0.3, -4.5)
	scene.add(gardenFill)

	return {
		windowGlow
	}
}

function createParts(root, materials) {
	const parts = {}

	addWallFace(root, parts, 'wall_face', materials.wall)
	addBox(root, parts, 'wall_left', materials.wall)
	addBox(root, parts, 'wall_right', materials.wall)
	addBox(root, parts, 'wall_top', materials.wall)
	addBox(root, parts, 'wall_bottom', materials.wall)

	addBox(root, parts, 'reveal_left', materials.revealLeft)
	addBox(root, parts, 'reveal_right', materials.revealRight)
	addBox(root, parts, 'reveal_top', materials.revealTop)
	addBox(root, parts, 'reveal_bottom', materials.revealBottom)

	addPlane(root, parts, 'top_shadow', materials.shadow)
	addPlane(root, parts, 'left_shadow', materials.sideShadow)
	addPlane(root, parts, 'right_shadow', materials.sideShadow)
	addPlane(root, parts, 'sill_highlight', materials.highlight)

	addBox(root, parts, 'window_frame_left', materials.frame, true)
	addBox(root, parts, 'window_frame_right', materials.frame, true)
	addBox(root, parts, 'window_frame_top', materials.frame, true)
	addBox(root, parts, 'window_frame_bottom', materials.frame, true)
	addBox(root, parts, 'window_middle_rail', materials.frame, true)
	addBox(root, parts, 'window_center_mullion', materials.frame, true)

	addBox(root, parts, 'glass_upper', materials.upperGlass, false)
	addBox(root, parts, 'glass_lower_left', materials.lowerGlass, false)
	addBox(root, parts, 'glass_lower_right', materials.lowerGlass, false)
	addPlane(root, parts, 'outdoor_lower_left', materials.outdoor)
	addPlane(root, parts, 'outdoor_lower_right', materials.outdoor)

	addBox(root, parts, 'roller_case', materials.frame, true)
	addCylinderX(root, parts, 'roller_tube', materials.frame)
	addBox(root, parts, 'motor_cap', materials.hardware, true)
	addBox(root, parts, 'manual_chain_left', materials.chain, false)
	addBox(root, parts, 'manual_chain_right', materials.chain, false)
	addTorus(root, parts, 'manual_chain_loop', materials.chain)

	addCurtain(root, parts, 'curtain_panel', materials.fabric)
	addCylinderX(root, parts, 'curtain_bottom_bar', materials.frame)
	addPlane(root, parts, 'curtain_soft_shadow', materials.curtainShadow)

	return parts
}

function addBox(root, parts, name, material, rounded = false) {
	const geometry = rounded ?
		new RoundedBoxGeometry(1, 1, 1, 5, 0.025) :
		new THREE.BoxGeometry(1, 1, 1)
	const mesh = new THREE.Mesh(geometry, material)
	mesh.name = name
	mesh.castShadow = true
	mesh.receiveShadow = true
	root.add(mesh)
	parts[name] = mesh
}

function addWallFace(root, parts, name, material) {
	const mesh = new THREE.Mesh(new THREE.BufferGeometry(), material)
	mesh.name = name
	mesh.receiveShadow = true
	root.add(mesh)
	parts[name] = mesh
}

function addPlane(root, parts, name, material) {
	const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material)
	mesh.name = name
	mesh.receiveShadow = true
	root.add(mesh)
	parts[name] = mesh
}

function addCylinderX(root, parts, name, material) {
	const geometry = new THREE.CylinderGeometry(0.5, 0.5, 1, 48, 1, false)
	geometry.rotateZ(Math.PI / 2)
	const mesh = new THREE.Mesh(geometry, material)
	mesh.name = name
	mesh.castShadow = true
	mesh.receiveShadow = true
	root.add(mesh)
	parts[name] = mesh
}

function addTorus(root, parts, name, material) {
	const geometry = new THREE.TorusGeometry(0.07, 0.008, 8, 32)
	const mesh = new THREE.Mesh(geometry, material)
	mesh.name = name
	mesh.castShadow = true
	root.add(mesh)
	parts[name] = mesh
}

function addCurtain(root, parts, name, material) {
	const mesh = new THREE.Mesh(createCurtainGeometry(), material)
	mesh.name = name
	mesh.castShadow = true
	mesh.receiveShadow = true
	root.add(mesh)
	parts[name] = mesh
}

function createMaterials() {
	const fabricTexture = makeFabricTexture()
	const outdoorTexture = makeOutdoorTexture()

	fabricTexture.wrapS = THREE.RepeatWrapping
	fabricTexture.wrapT = THREE.RepeatWrapping
	fabricTexture.repeat.set(3.2, 1.25)

	return {
		wall: new THREE.MeshStandardMaterial({
			color: '#d1d2ce',
			roughness: 0.95,
			metalness: 0,
			side: THREE.DoubleSide
		}),
		revealLeft: new THREE.MeshStandardMaterial({
			color: '#ecece7',
			roughness: 0.96,
			metalness: 0
		}),
		revealRight: new THREE.MeshStandardMaterial({
			color: '#c2c4bf',
			roughness: 0.97,
			metalness: 0
		}),
		revealTop: new THREE.MeshStandardMaterial({
			color: '#bfc1bd',
			roughness: 0.97,
			metalness: 0
		}),
		revealBottom: new THREE.MeshStandardMaterial({
			color: '#f4f4ef',
			roughness: 0.9,
			metalness: 0
		}),
		frame: new THREE.MeshStandardMaterial({
			color: '#090b0a',
			roughness: 0.34,
			metalness: 0.34
		}),
		hardware: new THREE.MeshStandardMaterial({
			color: '#111413',
			roughness: 0.42,
			metalness: 0.28
		}),
		chain: new THREE.MeshStandardMaterial({
			color: '#232827',
			roughness: 0.52,
			metalness: 0.24
		}),
		upperGlass: new THREE.MeshPhysicalMaterial({
			color: '#ffffff',
			roughness: 0.16,
			metalness: 0,
			transparent: true,
			opacity: 0.18,
			depthWrite: false,
			clearcoat: 0.58,
			clearcoatRoughness: 0.12
		}),
		lowerGlass: new THREE.MeshPhysicalMaterial({
			color: '#e5ecd8',
			roughness: 0.18,
			metalness: 0,
			transparent: true,
			opacity: 0.64,
			depthWrite: false,
			clearcoat: 0.52,
			clearcoatRoughness: 0.14
		}),
		outdoor: new THREE.MeshBasicMaterial({
			map: outdoorTexture,
			color: '#ffffff',
			side: THREE.DoubleSide,
			toneMapped: false
		}),
		fabric: new THREE.MeshStandardMaterial({
			color: '#f4f1ea',
			roughness: 0.92,
			metalness: 0,
			map: fabricTexture,
			bumpMap: fabricTexture,
			// bumpScale: 0.012,
			bumpScale: 0,
			side: THREE.DoubleSide,
			transparent: true,
			opacity: 0.96
		}),
		shadow: new THREE.MeshBasicMaterial({
			color: '#000000',
			side: THREE.DoubleSide,
			transparent: true,
			opacity: 0.22,
			depthWrite: false
		}),
		sideShadow: new THREE.MeshBasicMaterial({
			color: '#000000',
			side: THREE.DoubleSide,
			transparent: true,
			opacity: 0.13,
			depthWrite: false
		}),
		highlight: new THREE.MeshBasicMaterial({
			color: '#ffffff',
			side: THREE.DoubleSide,
			transparent: true,
			opacity: 0.48,
			depthWrite: false
		}),
		curtainShadow: new THREE.MeshBasicMaterial({
			color: '#000000',
			side: THREE.DoubleSide,
			transparent: true,
			opacity: 0.16,
			depthWrite: false
		})
	}
}

function buildLayout(config) {
	const widthCm = clamp(Number(config.width) || 280, RANGE.width[0], RANGE.width[1])
	const heightCm = clamp(Number(config.height) || 220, RANGE.height[0], RANGE.height[1])
	const frameW = mapRange(widthCm, RANGE.width[0], RANGE.width[1], 2.0, 5.0)
	const frameH = mapRange(heightCm, RANGE.height[0], RANGE.height[1], 1.55, 3.85)
	const reveal = RECESS_SIDE_WALL_WIDTH
	const lightingReveal = RECESS_LIGHTING_REVEAL
	const wallDepth = 0.52
	const sideMargin = Math.max(2.45, frameW * 0.8)
	const topMargin = Math.max(1.38, frameH * 0.48)
	const bottomMargin = Math.max(1.16, frameH * 0.38)
	const recessW = frameW + reveal * 2
	const recessH = frameH + reveal * 2
	const wallW = recessW + sideMargin * 2
	const wallH = recessH + topMargin + bottomMargin
	const focusW = frameW + lightingReveal * 2 + 1.0
	const focusH = frameH + lightingReveal * 2 + 0.86
	const sideRail = clamp(frameW * 0.035, 0.11, 0.17)
	const topRail = clamp(frameH * 0.065, 0.14, 0.23)
	const bottomRail = clamp(frameH * 0.06, 0.13, 0.21)
	const middleRail = 0.11
	const mullion = 0.13
	const innerW = frameW - sideRail * 2
	const middleY = -frameH * 0.27
	const upperTop = frameH / 2 - topRail - 0.055
	const upperBottom = middleY + middleRail / 2 + 0.055
	const lowerTop = middleY - middleRail / 2 - 0.055
	const lowerBottom = -frameH / 2 + bottomRail + 0.07
	const upperGlassH = Math.max(0.28, upperTop - upperBottom)
	const lowerGlassH = Math.max(0.26, lowerTop - lowerBottom)
	const lowerGlassW = Math.max(0.42, (innerW - mullion - 0.12) / 2)

	const outsideMount = config.mount === 'outside'

	// 外装时，窗帘和上方卷轴使用同一套宽度和中心点，不再按窗洞宽度单独计算config.mount === 'outside' ? -layout.rollerW / 2
	const rollerW = outsideMount ?
		frameW + OUTSIDE_CURTAIN_OVERLAP :
		frameW - INSIDE_CURTAIN_CLEARANCE
	const rollerX = 0
	const rollerTubeW = Math.max(0.6, rollerW)

	const rollerCaseH = outsideMount ? 0.26 : 0.2

	const rollerY = outsideMount ?
		frameH / 2 + 0.22 :
		frameH / 2 - rollerCaseH / 2

	const rollerZ = outsideMount ? -0.44 : -0.42

	const rollerTubeY = rollerY - 0.02
	const rollerTubeR = 0.064

	const curtainX = rollerX
	const curtainW = rollerTubeW
	// 外装：窗帘顶部塞进卷轴盒底部，视觉上成为一体；内装仍贴卷轴圆管下沿
	const curtainTop = outsideMount ?
		rollerY - rollerCaseH / 2 + 0.012 :
		rollerTubeY - rollerTubeR + 0.006

	const curtainBottomLimit = -frameH / 2 - 0.15
	const drop = clamp(Number(config.dropPercent) || 0, 0, 100) / 100
	const curtainH = Math.max(0, (curtainTop - curtainBottomLimit) * drop)
	const curtainY = curtainTop - curtainH / 2
	const curtainBottomY = curtainTop - curtainH
	// 外装时把帘布放到卷轴盒前沿同一视觉平面，避免透视下看起来比卷轴窄
	const curtainZ = outsideMount ? rollerZ - 0.105 : -0.4

	return {
		rollerX,
		rollerTubeW,
		rollerTubeY,
		rollerTubeR,

		widthCm,
		heightCm,
		frameW,
		frameH,
		wallW,
		wallH,
		focusW,
		focusH,
		wallDepth,
		recessW,
		recessH,
		reveal,
		lightingReveal,
		sideMargin,
		topMargin,
		bottomMargin,
		sideRail,
		topRail,
		bottomRail,
		middleRail,
		mullion,
		innerW,
		middleY,
		upperGlassH,
		lowerGlassH,
		lowerGlassW,
		lowerGlassLeftX: -(lowerGlassW + mullion) / 2,
		lowerGlassRightX: (lowerGlassW + mullion) / 2,
		upperGlassY: (upperTop + upperBottom) / 2,
		lowerGlassY: (lowerTop + lowerBottom) / 2,
		rollerW,
		rollerY,
		rollerZ,
		rollerCaseH,
		curtainX,
		curtainTop,
		curtainH,
		curtainW,
		curtainY,
		curtainBottomY,
		curtainZ
	}
}

function applyLayout(parts, layout, config) {
	const zWall = -layout.wallDepth / 2
	const zWallFace = -layout.wallDepth - 0.006
	const zReveal = -layout.wallDepth / 2 + 0.01
	const frameZ = -0.16
	const glassZ = -0.23
	const outsideZ = -0.34

	placeBox(
		parts.wall_left,
		layout.sideMargin,
		layout.wallH,
		layout.wallDepth,
		-layout.recessW / 2 - layout.sideMargin / 2,
		0,
		zWall
	)
	placeBox(
		parts.wall_right,
		layout.sideMargin,
		layout.wallH,
		layout.wallDepth,
		layout.recessW / 2 + layout.sideMargin / 2,
		0,
		zWall
	)
	placeBox(
		parts.wall_top,
		layout.wallW,
		layout.topMargin,
		layout.wallDepth,
		0,
		layout.recessH / 2 + layout.topMargin / 2,
		zWall
	)
	placeBox(
		parts.wall_bottom,
		layout.wallW,
		layout.bottomMargin,
		layout.wallDepth,
		0,
		-layout.recessH / 2 - layout.bottomMargin / 2,
		zWall
	)

	updateWallFace(parts.wall_face, layout.wallW + 1.8, layout.wallH + 1.6, layout.recessW, layout.recessH, zWallFace)

	placeBox(parts.reveal_left, layout.reveal, layout.recessH, layout.wallDepth, -layout.frameW / 2 - layout.reveal / 2,
		0, zReveal)
	placeBox(parts.reveal_right, layout.reveal, layout.recessH, layout.wallDepth, layout.frameW / 2 + layout.reveal / 2,
		0, zReveal)
	placeBox(parts.reveal_top, layout.frameW, layout.reveal, layout.wallDepth, 0, layout.frameH / 2 + layout.reveal / 2,
		zReveal)
	placeBox(parts.reveal_bottom, layout.frameW, layout.reveal, layout.wallDepth, 0, -layout.frameH / 2 - layout.reveal /
		2, zReveal)

	placePlane(parts.top_shadow, layout.frameW + 0.08, 0.08, 0, layout.frameH / 2 + 0.015, 0.018)
	placePlane(parts.left_shadow, 0.045, layout.frameH + 0.04, -layout.frameW / 2 - 0.006, 0, 0.018)
	placePlane(parts.right_shadow, 0.045, layout.frameH + 0.04, layout.frameW / 2 + 0.006, 0, 0.018)
	placePlane(parts.sill_highlight, layout.frameW + 0.08, 0.04, 0, -layout.frameH / 2 - layout.lightingReveal + 0.045,
		0.02)

	placeBox(parts.window_frame_left, layout.sideRail, layout.frameH, 0.18, -layout.frameW / 2 + layout.sideRail / 2, 0,
		frameZ)
	placeBox(parts.window_frame_right, layout.sideRail, layout.frameH, 0.18, layout.frameW / 2 - layout.sideRail / 2, 0,
		frameZ)
	placeBox(parts.window_frame_top, layout.frameW, layout.topRail, 0.18, 0, layout.frameH / 2 - layout.topRail / 2,
		frameZ)
	placeBox(parts.window_frame_bottom, layout.frameW, layout.bottomRail, 0.18, 0, -layout.frameH / 2 + layout
		.bottomRail / 2, frameZ)
	placeBox(parts.window_middle_rail, layout.innerW, layout.middleRail, 0.14, 0, layout.middleY, frameZ + 0.01)
	placeBox(parts.window_center_mullion, layout.mullion, layout.lowerGlassH + 0.12, 0.14, 0, layout.lowerGlassY, frameZ +
		0.015)

	placeBox(parts.glass_upper, layout.innerW - 0.06, layout.upperGlassH, 0.035, 0, layout.upperGlassY, glassZ)
	placeBox(parts.glass_lower_left, layout.lowerGlassW, layout.lowerGlassH, 0.035, layout.lowerGlassLeftX, layout
		.lowerGlassY, glassZ)
	placeBox(parts.glass_lower_right, layout.lowerGlassW, layout.lowerGlassH, 0.035, layout.lowerGlassRightX, layout
		.lowerGlassY, glassZ)
	placePlane(parts.outdoor_lower_left, layout.lowerGlassW, layout.lowerGlassH, layout.lowerGlassLeftX, layout
		.lowerGlassY, outsideZ)
	placePlane(parts.outdoor_lower_right, layout.lowerGlassW, layout.lowerGlassH, layout.lowerGlassRightX, layout
		.lowerGlassY, outsideZ)

	placeBox(parts.roller_case, layout.rollerW, layout.rollerCaseH, 0.2, layout.rollerX, layout.rollerY, layout.rollerZ)

	placeCylinderX(
		parts.roller_tube,
		layout.rollerTubeW,
		layout.rollerTubeR,
		layout.rollerX,
		layout.rollerTubeY,
		layout.rollerZ + 0.025
	)

	const motorX = layout.rollerX + (config.mount === 'outside' ? -layout.rollerW / 2 - 0.1 : -layout.rollerW / 2 + 0.12)
	placeBox(parts.motor_cap, 0.2, 0.2, 0.2, motorX, layout.rollerY - 0.004, layout.rollerZ + 0.002)

	const chainX = layout.rollerX - layout.rollerW / 2 - 0.22
	const chainH = Math.max(0.48, Math.min(0.92, layout.frameH * 0.34))
	const chainY = layout.rollerY - chainH / 2 - 0.14
	placeBox(parts.manual_chain_left, 0.014, chainH, 0.014, chainX - 0.045, chainY, layout.rollerZ + 0.04)
	placeBox(parts.manual_chain_right, 0.014, chainH, 0.014, chainX + 0.045, chainY, layout.rollerZ + 0.04)
	parts.manual_chain_loop.position.set(chainX, chainY - chainH / 2, layout.rollerZ + 0.04)

	const showCurtain = layout.curtainH > 0.015
	parts.curtain_panel.visible = showCurtain
	// 暂时隐藏窗帘底部配重轴
	// parts.curtain_bottom_bar.visible = showCurtain
	parts.curtain_bottom_bar.visible = false
	parts.curtain_soft_shadow.visible = showCurtain

	if (showCurtain) {
		parts.curtain_panel.position.set(layout.curtainX, layout.curtainY, layout.curtainZ)
		parts.curtain_panel.scale.set(layout.curtainW, layout.curtainH, 1)

		// 暂时隐藏窗帘底部配重轴
		// placeCylinderX(
		// 	parts.curtain_bottom_bar,
		// 	layout.curtainW,
		// 	0.043,
		// 	layout.curtainX,
		// 	layout.curtainBottomY,
		// 	layout.curtainZ - 0.03
		// )

		placePlane(
			parts.curtain_soft_shadow,
			layout.curtainW + 0.04,
			layout.curtainH,
			layout.curtainX,
			layout.curtainY - 0.02,
			layout.curtainZ + 0.055
		)

	}

	const electric = config.control === 'motor'
	parts.motor_cap.visible = electric
	parts.manual_chain_left.visible = !electric
	parts.manual_chain_right.visible = !electric
	parts.manual_chain_loop.visible = !electric
}

function fitCamera(camera, controls, layout, container) {
	const aspect = Math.max(container.clientWidth, 1) / Math.max(container.clientHeight, 1)
	const fov = THREE.MathUtils.degToRad(camera.fov)
	const paddedW = layout.focusW
	const paddedH = layout.focusH
	const distanceForHeight = paddedH / (2 * Math.tan(fov / 2))
	const distanceForWidth = paddedW / (2 * Math.tan(fov / 2) * aspect)
	const distance = Math.max(distanceForHeight, distanceForWidth, 5.4)
	const cameraX = layout.wallW * 0.045
	const cameraY = layout.wallH * 0.018

	camera.position.set(cameraX, cameraY, distance)
	controls.target.set(0, 0, -0.1)
	controls.minDistance = distance * 0.74
	controls.maxDistance = distance * 1.38
	camera.lookAt(controls.target)
	camera.updateProjectionMatrix()
}

function updateMaterialColors(materials, config) {
	const wall = config.wallColor || '#d1d2ce'
	materials.wall.color.set(wall)
	materials.revealLeft.color.set(shade(wall, 0.15))
	materials.revealRight.color.set(shade(wall, -0.06))
	materials.revealTop.color.set(shade(wall, -0.12))
	materials.revealBottom.color.set(shade(wall, 0.2))
	materials.fabric.color.set(config.curtainColor || '#f4f1ea')
}

function placeBox(mesh, width, height, depth, x, y, z) {
	mesh.position.set(x, y, z)
	mesh.scale.set(width, height, depth)
}

function placePlane(mesh, width, height, x, y, z) {
	mesh.position.set(x, y, z)
	mesh.scale.set(width, height, 1)
}

function placeCylinderX(mesh, length, radius, x, y, z) {
	mesh.position.set(x, y, z)
	mesh.scale.set(length, radius * 2, radius * 2)
}

function updateWallFace(mesh, outerWidth, outerHeight, holeWidth, holeHeight, z) {
	if (!mesh) return

	if (mesh.geometry) {
		mesh.geometry.dispose()
	}

	mesh.geometry = createWallFaceGeometry(outerWidth, outerHeight, holeWidth, holeHeight)
	mesh.position.set(0, 0, z)
}

function createWallFaceGeometry(outerWidth, outerHeight, holeWidth, holeHeight) {
	const x0 = -outerWidth / 2
	const x1 = -holeWidth / 2
	const x2 = holeWidth / 2
	const x3 = outerWidth / 2
	const y0 = -outerHeight / 2
	const y1 = -holeHeight / 2
	const y2 = holeHeight / 2
	const y3 = outerHeight / 2

	const rectangles = [
		[x0, x1, y0, y3],
		[x2, x3, y0, y3],
		[x1, x2, y2, y3],
		[x1, x2, y0, y1]
	]
	const positions = []
	const indices = []

	rectangles.forEach(([left, right, bottom, top], index) => {
		const start = index * 4
		positions.push(
			left, bottom, 0,
			right, bottom, 0,
			right, top, 0,
			left, top, 0
		)
		indices.push(start, start + 1, start + 2, start, start + 2, start + 3)
	})

	const geometry = new THREE.BufferGeometry()
	geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
	geometry.setIndex(indices)
	geometry.computeVertexNormals()
	return geometry
}

// function createCurtainGeometry() {
// 	const columns = 72
// 	const rows = 18
// 	const positions = []
// 	const uvs = []
// 	const indices = []

// 	for (let row = 0; row <= rows; row += 1) {
// 		const v = row / rows
// 		for (let column = 0; column <= columns; column += 1) {
// 			const u = column / columns
// 			const x = u - 0.5
// 			const y = 0.5 - v
// 			const primaryFold = Math.sin(u * Math.PI * 18) * 0.034
// 			const secondaryFold = Math.sin(u * Math.PI * 36 + 0.5) * 0.009
// 			const bottomSoftness = 0.72 + v * 0.28
// 			const z = (primaryFold + secondaryFold) * bottomSoftness
// 			positions.push(x, y, z)
// 			uvs.push(u, v)
// 		}
// 	}

// 	for (let row = 0; row < rows; row += 1) {
// 		for (let column = 0; column < columns; column += 1) {
// 			const a = row * (columns + 1) + column
// 			const b = a + 1
// 			const c = a + columns + 1
// 			const d = c + 1
// 			indices.push(a, c, b, b, c, d)
// 		}
// 	}

// 	const geometry = new THREE.BufferGeometry()
// 	geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
// 	geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
// 	geometry.setIndex(indices)
// 	geometry.computeVertexNormals()
// 	return geometry
// }

function createCurtainGeometry() {
	const columns = 12
	const rows = 8
	const positions = []
	const uvs = []
	const indices = []

	for (let row = 0; row <= rows; row += 1) {
		const v = row / rows

		for (let column = 0; column <= columns; column += 1) {
			const u = column / columns

			const x = u - 0.5
			const y = 0.5 - v

			// 平整窗帘：取消波浪褶皱
			const z = 0

			positions.push(x, y, z)
			uvs.push(u, v)
		}
	}

	for (let row = 0; row < rows; row += 1) {
		for (let column = 0; column < columns; column += 1) {
			const a = row * (columns + 1) + column
			const b = a + 1
			const c = a + columns + 1
			const d = c + 1

			indices.push(a, c, b, b, c, d)
		}
	}

	const geometry = new THREE.BufferGeometry()
	geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
	geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
	geometry.setIndex(indices)
	geometry.computeVertexNormals()

	return geometry
}

function makePlasterTexture() {
	const size = 256
	const canvas = document.createElement('canvas')
	canvas.width = size
	canvas.height = size
	const ctx = canvas.getContext('2d')
	const image = ctx.createImageData(size, size)

	for (let y = 0; y < size; y += 1) {
		for (let x = 0; x < size; x += 1) {
			const i = (y * size + x) * 4
			const u = x / size
			const v = y / size
			const grain = pseudoNoise(x * 17 + y * 31) * 18
			const plaster = Math.sin(u * 92) * 4 + Math.sin((u + v) * 160) * 3
			const value = clamp(224 + grain + plaster, 170, 255)
			image.data[i] = value
			image.data[i + 1] = value
			image.data[i + 2] = value
			image.data[i + 3] = 255
		}
	}

	ctx.putImageData(image, 0, 0)
	const texture = new THREE.CanvasTexture(canvas)
	texture.colorSpace = THREE.SRGBColorSpace
	return texture
}

function makeFabricTexture() {
	const size = 256
	const canvas = document.createElement('canvas')
	canvas.width = size
	canvas.height = size
	const ctx = canvas.getContext('2d')

	ctx.fillStyle = '#f4f1ea'
	ctx.fillRect(0, 0, size, size)

	for (let x = 0; x < size; x += 4) {
		ctx.fillStyle = x % 16 === 0 ? 'rgba(130,125,112,0.12)' : 'rgba(255,255,255,0.13)'
		ctx.fillRect(x, 0, 1, size)
	}

	for (let y = 0; y < size; y += 7) {
		ctx.fillStyle = 'rgba(100,96,88,0.045)'
		ctx.fillRect(0, y, size, 1)
	}

	const texture = new THREE.CanvasTexture(canvas)
	texture.colorSpace = THREE.SRGBColorSpace
	return texture
}

function makeOutdoorTexture() {
	const width = 512
	const height = 256
	const canvas = document.createElement('canvas')
	canvas.width = width
	canvas.height = height
	const ctx = canvas.getContext('2d')
	const gradient = ctx.createLinearGradient(0, 0, 0, height)
	gradient.addColorStop(0, '#dfe9c9')
	gradient.addColorStop(0.52, '#c9ddad')
	gradient.addColorStop(1, '#eef0da')
	ctx.fillStyle = gradient
	ctx.fillRect(0, 0, width, height)

	for (let i = 0; i < 420; i += 1) {
		const x = pseudoNoise(i * 19) * width + width / 2
		const y = Math.abs(pseudoNoise(i * 31)) * height * 0.86
		const r = 8 + Math.abs(pseudoNoise(i * 43)) * 28
		ctx.fillStyle = i % 3 === 0 ? 'rgba(118,147,82,0.15)' : 'rgba(220,230,186,0.16)'
		ctx.beginPath()
		ctx.arc(x, y, r, 0, Math.PI * 2)
		ctx.fill()
	}

	ctx.fillStyle = 'rgba(184,159,120,0.42)'
	ctx.fillRect(0, height * 0.82, width, 16)
	ctx.fillStyle = 'rgba(255,255,255,0.24)'
	ctx.fillRect(0, height * 0.82, width, 2)

	const texture = new THREE.CanvasTexture(canvas)
	texture.colorSpace = THREE.SRGBColorSpace
	return texture
}

function shade(hex, amount) {
	const color = new THREE.Color(hex)
	color.offsetHSL(0, 0, amount)
	return `#${color.getHexString()}`
}

function mapRange(value, inMin, inMax, outMin, outMax) {
	return outMin + ((value - inMin) / (inMax - inMin)) * (outMax - outMin)
}

function pseudoNoise(seed) {
	const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453
	return value - Math.floor(value) - 0.5
}

function clamp(value, min, max) {
	return Math.min(max, Math.max(min, value))
}